import { useState, useEffect, useCallback } from 'react';
import { Printer, AlertTriangle, RotateCcw, Check, X, History } from 'lucide-react';

import { Card }    from '@/components/ui/Card';
import { Badge }   from '@/components/ui/Badge';
import { Button }  from '@/components/ui/Button';
import { Input }   from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { chequeApi }     from '@/api/cheque.api';
import { BatchPrintDialog } from '@/components/banking/BatchPrintDialog';
import type { ChequePrintQueueItem, ChequePrintLogEntry } from '@/api/cheque.api';
import { cn } from '@/utils/cn';

/**
 * Cheque print queue and confirmation (GAP-008).
 *
 * The layout engine could always render a cheque; nothing recorded that one was actually printed,
 * so there was no queue and no way to tell a duplicate from a first print. This screen is that
 * record: which issued cheques are still waiting, a confirm for the routine first print, and a
 * reprint that demands a reason — because a reprint means a second physical cheque could carry the
 * same number, and the reason is what an auditor reads when it turns up twice.
 */

export const ChequePrintQueuePage = () => {
  const { hasPermission } = usePermission();
  const canPrint   = hasPermission(Permissions.BNK_CHEQUE_PRINT);
  const canReprint = hasPermission(Permissions.BNK_CHEQUE_REPRINT);

  const [items,          setItems]          = useState<ChequePrintQueueItem[]>([]);
  const [includePrinted, setIncludePrinted] = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState<string | null>(null);
  const [busyId,         setBusyId]         = useState<string | null>(null);

  const [reprintFor, setReprintFor] = useState<ChequePrintQueueItem | null>(null);
  const [historyFor, setHistoryFor] = useState<ChequePrintQueueItem | null>(null);

  // Batch print (GAP-007). Only unprinted cheques are selectable — printing one again is a
  // reprint, which needs a reason and is a different action.
  const [selected,   setSelected]   = useState<Set<string>>(new Set());
  const [batchOpen,  setBatchOpen]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await chequeApi.printQueue({ includePrinted, pageSize: 200 });
      setItems(res.data.data?.items ?? []);
      setError(null);
    } catch {
      setError('Could not load the print queue. Reload the page, or contact your administrator if this continues.');
    } finally {
      setLoading(false);
    }
  }, [includePrinted]);

  useEffect(() => { void load(); }, [load]);

  const selectable = items.filter(i => i.printCount === 0);

  // Ticking "show already-printed" changes what is on screen, so a selection made before it would
  // be a set of ids the operator can no longer see. Clear it rather than print something invisible.
  useEffect(() => { setSelected(new Set()); }, [includePrinted]);

  const toggleOne = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  const allSelected = selectable.length > 0 && selectable.every(i => selected.has(i.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(i => i.id)));

  // Grid order, not click order: the operator loads the stationery in the order they see.
  const selectedItems = items.filter(i => selected.has(i.id));

  const readError = (e: unknown): string => {
    const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
    return res?.data?.errors?.[0]?.message ?? res?.data?.message ?? 'That did not work.';
  };

  const confirm = async (item: ChequePrintQueueItem) => {
    setBusyId(item.id); setError(null);
    try {
      await chequeApi.confirmPrint(item.id);
      await load();
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Printer className="w-5 h-5 text-orange-500" />
            Cheque Print Queue
          </h1>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1 max-w-3xl">
            Cheques you have issued and their print state. Confirm a cheque once it has printed
            correctly — that is what records the physical cheque exists. A reprint needs a reason,
            because it means a second cheque could carry the same number.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mt-1">
          <input type="checkbox" checked={includePrinted}
                 onChange={e => setIncludePrinted(e.target.checked)} />
          Show already-printed
        </label>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/* Action bar — appears only with a selection, so it never competes with the grid. */}
      {canPrint && selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded border border-orange-200 bg-orange-50 px-3 py-2 dark:border-orange-800 dark:bg-orange-900/20">
          <span className="text-sm text-orange-900 dark:text-orange-200">
            {selected.size} cheque{selected.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            <Button onClick={() => setBatchOpen(true)}>
              <Printer className="w-3.5 h-3.5 mr-1" /> Print Selected ({selected.size})
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center">
          <Printer className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
            {includePrinted ? 'No issued cheques' : 'Nothing waiting to print'}
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400 max-w-md mx-auto">
            {includePrinted
              ? 'No cheques have been issued from any bank account yet.'
              : 'Every issued cheque has been printed. Tick “Show already-printed” to see them.'}
          </p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400">
                {canPrint && (
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" aria-label="Select all printable cheques"
                           checked={allSelected}
                           disabled={selectable.length === 0}
                           onChange={toggleAll} />
                  </th>
                )}
                <th className="px-3 py-2 font-semibold">Cheque #</th>
                <th className="px-3 py-2 font-semibold">Date</th>
                <th className="px-3 py-2 font-semibold">Payee</th>
                <th className="px-3 py-2 font-semibold text-right">Amount</th>
                <th className="px-3 py-2 font-semibold">Bank account</th>
                <th className="px-3 py-2 font-semibold">Voucher</th>
                <th className="px-3 py-2 font-semibold">Print state</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-orange-50/40 dark:hover:bg-slate-700/30">
                  {canPrint && (
                    <td className="px-3 py-2">
                      {/* An already-printed cheque has no checkbox at all: including it would offer
                          a batch reprint with no reason, which is the one thing the log exists to
                          prevent. Reprint it individually. */}
                      {item.printCount === 0 && (
                        <input type="checkbox"
                               aria-label={`Select cheque ${item.chequeNumber}`}
                               checked={selected.has(item.id)}
                               onChange={() => toggleOne(item.id)} />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono text-slate-700 dark:text-slate-200">{item.chequeNumber}</td>
                  <td className="px-3 py-2 text-slate-500 dark:text-gray-400">{item.chequeDate.split('T')[0]}</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{item.payeeName || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                    {item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {item.currencyCode}
                  </td>
                  <td className="px-3 py-2 text-slate-500 dark:text-gray-400">{item.bankAccountCode}</td>
                  <td className="px-3 py-2 text-slate-500 dark:text-gray-400">{item.referenceDocument ?? '—'}</td>
                  <td className="px-3 py-2">
                    {item.printCount === 0 ? (
                      <Badge variant="warning">Not printed</Badge>
                    ) : item.printCount === 1 ? (
                      <Badge variant="success">Printed</Badge>
                    ) : (
                      // A reprinted cheque is the one worth flagging — more than one physical cheque
                      // may carry this number.
                      <Badge variant="danger">Printed ×{item.printCount}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      {item.printCount > 0 && (
                        <Button variant="ghost" onClick={() => setHistoryFor(item)}>
                          <History className="w-3.5 h-3.5 mr-1" /> History
                        </Button>
                      )}
                      {canPrint && item.printCount === 0 && (
                        <Button disabled={busyId === item.id} onClick={() => confirm(item)}>
                          <Check className="w-3.5 h-3.5 mr-1" /> Confirm print
                        </Button>
                      )}
                      {canReprint && item.printCount > 0 && (
                        <Button variant="secondary" disabled={busyId === item.id}
                                onClick={() => setReprintFor(item)}>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reprint
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reprintFor && (
        <ReprintDialog
          item={reprintFor}
          onCancel={() => setReprintFor(null)}
          onDone={async () => { setReprintFor(null); await load(); }}
        />
      )}

      {historyFor && (
        <HistoryDialog item={historyFor} onClose={() => setHistoryFor(null)} />
      )}

      {batchOpen && selectedItems.length > 0 && (
        <BatchPrintDialog
          items={selectedItems}
          onCancel={() => setBatchOpen(false)}
          onDone={async () => { setBatchOpen(false); setSelected(new Set()); await load(); }}
        />
      )}
    </div>
  );
};

// ── Reprint — needs a reason ─────────────────────────────────────────────────

const ReprintDialog = ({
  item, onCancel, onDone,
}: { item: ChequePrintQueueItem; onCancel: () => void; onDone: () => void }) => {
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await chequeApi.reprint(item.id, reason.trim());
      onDone();
    } catch (e: unknown) {
      const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
      setError(res?.data?.errors?.[0]?.message ?? res?.data?.message ?? 'Could not record the reprint.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md" onClick={e => e.stopPropagation()}>
        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">
              Reprint cheque {item.chequeNumber}
            </h3>
            <button onClick={onCancel} className="text-slate-400 hover:text-slate-600" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-slate-500 dark:text-gray-400">
            This cheque has already been printed {item.printCount === 1 ? 'once' : `${item.printCount} times`}.
            A reprint means a second physical cheque could carry this number — say why, so the record
            explains it.
          </p>
          <Input value={reason} onChange={e => setReason(e.target.value)}
                 placeholder="Jammed in the printer, first copy destroyed" autoFocus />
          {error && (
            <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button variant="danger" disabled={busy || !reason.trim()} onClick={submit}>
              Record reprint
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// ── Print history ─────────────────────────────────────────────────────────────

const HistoryDialog = ({
  item, onClose,
}: { item: ChequePrintQueueItem; onClose: () => void }) => {
  const [logs,    setLogs]    = useState<ChequePrintLogEntry[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    chequeApi.printHistory(item.id)
      .then(r => setLogs(r.data.data ?? []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [item.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">
              Print history — cheque {item.chequeNumber}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : (
            <ol className="space-y-2">
              {(logs ?? []).map(log => (
                <li key={log.id} className={cn(
                  'rounded border p-3 text-sm',
                  log.isReprint
                    ? 'border-rose-200 bg-rose-50/60 dark:border-rose-800 dark:bg-rose-900/20'
                    : 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-800/40')}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      Print #{log.printSequence}{log.isReprint ? ' · reprint' : ''}
                    </span>
                    <span className="text-xs text-slate-400">
                      {log.printedDate.replace('T', ' ').slice(0, 16)}
                      {log.printedByName ? ` · ${log.printedByName}` : ''}
                    </span>
                  </div>
                  {/* The reprint reason is the record's whole purpose — show it plainly. */}
                  {log.reprintReason && (
                    <p className="mt-1 text-slate-600 dark:text-slate-300">{log.reprintReason}</p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </div>
  );
};
